import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArcElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartConfiguration,
} from "chart.js";
import { IconChargers } from "./icons.js";

Chart.register(ArcElement, CategoryScale, DoughnutController, Filler, Legend, LineController, LineElement, LinearScale, PointElement, Tooltip);

export interface DashboardOperationsData {
  generatedAt: string;
  trafico: Array<{ ts: string; sessoes: number; kwh: number }>;
  conectores: ConnectorSummary;
  garages: GarageSummary[];
}

interface ConnectorSummary {
  available: number;
  charging: number;
  faulted: number;
  outros: number;
  total: number;
}

interface GarageSummary {
  id: number | null;
  name: string;
  code: string | null;
  chargers: number;
  connectors: ConnectorSummary;
  chargerLayout: Array<{
    id: number;
    cpId: string;
    connectors: Array<{ connectorId: number; status: string }>;
  }>;
}

function readToken(token: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return value || fallback;
}

function formatHour(value: string): string {
  return new Date(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function useDashboardOperations() {
  const [data, setData] = useState<DashboardOperationsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await fetch("/api/dashboard/routines", { credentials: "include" });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = await response.json() as DashboardOperationsData;
        if (active) {
          setData(body);
          setError(null);
        }
      } catch (cause) {
        if (active) setError(cause instanceof Error ? cause.message : "Falha ao carregar a operação.");
      }
    };
    void load();
    const timer = window.setInterval(() => {
      if (!document.hidden) void load();
    }, 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  return { data, error };
}

export function DashboardOperationsPanel({ data, error }: { data: DashboardOperationsData | null; error: string | null }) {
  const energyRef = useRef<HTMLCanvasElement>(null);
  const connectorRef = useRef<HTMLCanvasElement>(null);

  const totalEnergy = useMemo(() => data?.trafico.reduce((sum, hour) => sum + hour.kwh, 0) ?? 0, [data]);
  const peak = useMemo(() => data?.trafico.reduce((current, hour) => hour.kwh > current.kwh ? hour : current, { ts: "", sessoes: 0, kwh: 0 }), [data]);

  useEffect(() => {
    if (!data || !energyRef.current || !connectorRef.current) return;

    const mute = readToken("--nx-mute", "#667085");
    const line = readToken("--nx-line", "#d9e0ea");
    const info = readToken("--nx-info-500", "#2586d7");
    const success = readToken("--nx-success-500", "#20b960");
    const warning = readToken("--nx-warning-500", "#ed9d17");
    const danger = readToken("--nx-danger-500", "#ef4444");
    const neutral = readToken("--nx-neutral-400", "#94a3b8");

    const energyChart = new Chart(energyRef.current, {
      type: "line",
      data: {
        labels: data.trafico.map((hour) => formatHour(hour.ts)),
        datasets: [{
          label: "kWh entregues",
          data: data.trafico.map((hour) => hour.kwh),
          borderColor: success,
          backgroundColor: `${success}20`,
          borderWidth: 2,
          fill: true,
          pointRadius: 0,
          tension: 0.32,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (context) => `${Number(context.parsed.y).toFixed(2)} kWh` } } },
        scales: {
          x: { grid: { color: line, drawTicks: false }, ticks: { color: mute, maxTicksLimit: 7, font: { size: 10 } } },
          y: { beginAtZero: true, grid: { color: line }, ticks: { color: mute, font: { size: 10 }, callback: (value) => `${value} kWh` } },
        },
      },
    } satisfies ChartConfiguration<"line">);

    const connectorChart = new Chart(connectorRef.current, {
      type: "doughnut",
      data: {
        labels: ["Carregando", "Disponível", "Com falha", "Outro estado"],
        datasets: [{
          data: [data.conectores.charging, data.conectores.available, data.conectores.faulted, data.conectores.outros],
          backgroundColor: [info, success, danger, neutral],
          borderWidth: 0,
          spacing: 2,
        }],
      },
      options: { responsive: true, maintainAspectRatio: false, cutout: "68%", animation: false, plugins: { legend: { display: false } } },
    } satisfies ChartConfiguration<"doughnut">);

    return () => {
      energyChart.destroy();
      connectorChart.destroy();
    };
  }, [data]);

  if (!data) {
    return <section className="nx-dashboard-operations" aria-label="Visão operacional"><div className="nx-dashboard-panel nx-dashboard-panel--loading">{error ? `Falha ao carregar visão operacional: ${error}` : "Carregando visão operacional…"}</div></section>;
  }

  return <section className="nx-dashboard-operations" aria-label="Visão operacional">
    <div className="nx-dashboard-section-head"><div><h2>Operação por garagem</h2><p>Estado atual dos conectores físicos por local.</p></div><span>{data.garages.length} {data.garages.length === 1 ? "local" : "locais"}</span></div>
    <div className="nx-dashboard-garages">
      {data.garages.map((garage) => <GarageCard garage={garage} key={garage.id ?? "unassigned"} />)}
    </div>
    <div className="nx-dashboard-charts">
      <article className="nx-dashboard-panel nx-dashboard-panel--energy">
        <header><div><h2>Energia entregue (24h)</h2><p>kWh por hora, a partir de sessões OCPP encerradas.</p></div><strong>{totalEnergy.toFixed(1)} kWh</strong></header>
        <div className="nx-dashboard-chart"><canvas ref={energyRef} /></div>
        <footer>{peak?.kwh ? <>Pico: <b>{formatHour(peak.ts)}</b> ({peak.kwh.toFixed(1)} kWh)</> : "Sem energia medida em sessões encerradas no período."}</footer>
      </article>
      <article className="nx-dashboard-panel nx-dashboard-panel--connector">
        <header><div><h2>Status dos conectores</h2><p>{data.conectores.total} conectores físicos monitorados.</p></div></header>
        <div className="nx-dashboard-donut"><div className="nx-dashboard-donut__chart"><canvas ref={connectorRef} /><strong>{data.conectores.total}</strong><span>conectores</span></div><ConnectorLegend connectors={data.conectores} /></div>
      </article>
    </div>
  </section>;
}

function GarageCard({ garage }: { garage: GarageSummary }) {
  const { connectors } = garage;
  return <article className="nx-dashboard-garage">
    <header><span className="nx-dashboard-garage__icon"><IconChargers /></span><div><h3>{garage.name}</h3><p>{garage.chargers} {garage.chargers === 1 ? "carregador" : "carregadores"} · {connectors.total} {connectors.total === 1 ? "conector" : "conectores"}</p></div></header>
    <div className="nx-dashboard-garage__metrics"><Metric label="Carregando" value={connectors.charging} tone="charging" /><Metric label="Disponíveis" value={connectors.available} tone="available" /><Metric label="Outros" value={connectors.outros} tone="other" /><Metric label="Com falha" value={connectors.faulted} tone="faulted" /></div>
    <ConnectorStrip chargers={garage.chargerLayout} />
  </article>;
}

function Metric({ label, value, tone }: { label: string; value: number; tone: "charging" | "available" | "other" | "faulted" }) {
  return <div className={`nx-dashboard-metric nx-dashboard-metric--${tone}`}><strong>{value}</strong><span>{label}</span></div>;
}

type StripTone = "available" | "charging" | "suspended" | "preparing" | "faulted" | "other";

function connectorStripTone(status: string): StripTone {
  if (status === "Available") return "available";
  if (status === "Charging") return "charging";
  if (status === "SuspendedEV" || status === "SuspendedEVSE") return "suspended";
  if (status === "Preparing" || status === "Finishing") return "preparing";
  if (status === "Faulted" || status === "Unavailable") return "faulted";
  return "other";
}

function ConnectorStrip({ chargers }: { chargers: GarageSummary["chargerLayout"] }) {
  if (!chargers.length) return <div className="nx-dashboard-connectors"><span>Sem conectores cadastrados.</span></div>;

  return <div className="nx-dashboard-connector-strip" aria-label={`${chargers.length} carregadores exibidos por conector`}>
    <div className="nx-dashboard-connectors">
      {chargers.map((charger, index) => {
        const physical = [...charger.connectors].sort((a, b) => a.connectorId - b.connectorId);
        const slots = physical.length ? physical : [{ connectorId: 0, status: "Unknown" }];
        while (slots.length < 2) slots.push({ connectorId: 0, status: "Unknown" });
        return <div className="nx-dashboard-connector-unit" key={charger.id} title={charger.cpId}>
          <span className="nx-dashboard-connector-unit__label">{index + 1}</span>
          <span className="nx-dashboard-connector-pair">
            {slots.map((connector, slot) => <i
              aria-label={`${charger.cpId}, conector ${connector.connectorId || slot + 1}: ${connector.status}`}
              className={`nx-dashboard-connector nx-dashboard-connector--${connectorStripTone(connector.status)}`}
              key={`${charger.id}-${connector.connectorId}-${slot}`}
            />)}
          </span>
        </div>;
      })}
    </div>
    <ul className="nx-dashboard-strip-legend" aria-label="Legenda dos estados">
      <li><i className="nx-dashboard-connector nx-dashboard-connector--available" />Disponível</li>
      <li><i className="nx-dashboard-connector nx-dashboard-connector--charging" />Carregando</li>
      <li><i className="nx-dashboard-connector nx-dashboard-connector--suspended" />Suspenso</li>
      <li><i className="nx-dashboard-connector nx-dashboard-connector--preparing" />Prep./Final.</li>
      <li><i className="nx-dashboard-connector nx-dashboard-connector--faulted" />Falha</li>
    </ul>
  </div>;
}

function ConnectorLegend({ connectors }: { connectors: ConnectorSummary }) {
  const rows = [["Carregando", connectors.charging, "charging"], ["Disponível", connectors.available, "available"], ["Com falha", connectors.faulted, "faulted"], ["Outro estado", connectors.outros, "other"]] as const;
  return <ul className="nx-dashboard-legend">{rows.map(([label, value, tone]) => <li key={tone}><i className={`nx-dashboard-connector nx-dashboard-connector--${tone}`} />{label}<b>{value}</b></li>)}</ul>;
}
